import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { MEMBER_PRIORITY_TASKS_QUERY_KEY } from './queries/useMemberPriorityTasks';
import { MEMBER_TASK_GROUPS_QUERY_KEY } from './queries/useMemberTaskGroups';
import { MEMBER_TASKS_QUERY_KEY } from './queries/useMemberTasks';

/**
 * Returns a callback that invalidates the right member-task surfaces after a completion, keyed off
 * the completed task's `group_id` (a backend field on every member task — see Task.group_id):
 *
 * - Ungrouped task (`group_id` null/absent) → invalidate all three surfaces so the task drops from
 *   the feed + priority carousel and any group counts refresh.
 * - Grouped task (`group_id` set) → invalidate only the task list. The group card + priority feed
 *   are deliberately left stale so the panel's card isn't yanked mid-flow; they refresh when the
 *   panel closes (see useGroupAction).
 *
 * Kept in one place so a new completion path can't silently miss the branch. Returns the combined
 * promise so callers can await the refetch.
 */
export default function useInvalidateMemberTaskQueries() {
    const queryClient = useQueryClient();

    return useCallback(
        ({ group_id }: { group_id?: string | null }) => {
            if (group_id) {
                return queryClient.invalidateQueries({ queryKey: MEMBER_TASKS_QUERY_KEY });
            }
            return Promise.all([
                queryClient.invalidateQueries({ queryKey: MEMBER_TASKS_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: MEMBER_TASK_GROUPS_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: MEMBER_PRIORITY_TASKS_QUERY_KEY }),
            ]);
        },
        [queryClient]
    );
}
